import * as React from 'react'
import 'core-js/proposals/array-grouping-v2'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import { graphql, navigate } from 'gatsby'
import { useState, useEffect } from 'react'
import MastHead from '../components/organisms/MastHead/masthead'
import ProductCard from '../components/organisms/ProductCard/ProductCard'
import Breadcrumbs from '../components/organisms/Breadcrumbs/Breadcrumbs'
import { createWebpageSchema } from '../helpers/createWebpageSchema'

import nodeApi from '../utils/api/nodeApi/nodeApi'
import Lockup from '../components/organisms/TextAndImageLockup/TextAndImageLockup'
import { createItemListSchema } from '../helpers/createItemListSchema'

function flatten(arr) {
  let flat = []
  for (let i = 0; i < arr.length; i++) {
    flat = flat.concat(arr[i].node.products)
  }
  return flat
}

function generateCollectionData(data, isSoftware) {
  const dronesGroup = []
  const rentalGroup = []
  const repairGroup = []
  const trainingGroup = []
  const softwareGroup = []
  let accessoriesGroupArray = []

  if (data?.length) {
    const flattenArr = flatten(data)

    flattenArr
      .filter(
        (value, index, self) =>
          index === self.findIndex((obj) => obj.id === value.id) &&
          (process.env.GATSBY_STAGE === 'production' ? !value.tags.includes('staging') : true)
      )
      .forEach((node) => {
        switch (node.productType) {
          case 'Software':
            softwareGroup.push(node)
            break
          case 'Course':
            trainingGroup.push(node)
            break
          case 'Drones':
            dronesGroup.push(node)
            break
          case 'Rental':
          case 'Rental Drones':
            rentalGroup.push(node)
            break
          case 'Repair':
          case 'Repair Refresh':
            repairGroup.push(node)
            break
          case 'Rental Payloads':
          case 'Rental Accessories':
          default:
            accessoriesGroupArray.push(node)
            break
        }
      })

    if (!isSoftware) {
      accessoriesGroupArray.unshift(...rentalGroup)
      accessoriesGroupArray.unshift(...dronesGroup)
      accessoriesGroupArray.push(...softwareGroup)
    }

    const groupedAccessories = Object.groupBy(
      accessoriesGroupArray,
      ({ productType }) => productType
    )
    accessoriesGroupArray = Object.entries(groupedAccessories).map(([key, value]) => ({
      title: key,
      value,
    }))
  }

  return { dronesGroup, accessoriesGroupArray, trainingGroup, softwareGroup, repairGroup }
}

// Gatsby Head
//---------------------------------
export function Head({ data, pageContext, location }) {
  const title = data.allContentfulPageProductFamily.edges[0].node.pageMetaTitle

  const description = data.allContentfulPageProductFamily.edges[0].node.pageMetaDescription
  const webPageSchema = createWebpageSchema({
    pageTitle: title,
    pageDescription: description,
    pageUrl: `${process.env.GATSBY_HELIGUY_DOMAIN}${location?.pathname}`,
    breadcrumbs: pageContext?.breadCrumbs,
  })

  const isSoftware = location?.pathname?.includes('drone-software')
  const allData = data.data.edges
  const { accessoriesGroupArray } = generateCollectionData(allData, isSoftware)

  const itemListSchema = createItemListSchema(accessoriesGroupArray)

  return (
    <>
      <html lang="en" />
      <meta charset="UTF-8" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content="" />
      <meta name="author" content="Heliguy" />
      <link rel="preconnect" href="https://images.ctfassets.net" crossOrigin />
      <link
        rel="canonical"
        href={`${process.env.GATSBY_HELIGUY_DOMAIN}${pageContext.breadcrumb.location}`}
      />
      {webPageSchema && <script type="application/ld+json">{webPageSchema}</script>}
      {itemListSchema && <script type="application/ld+json">{itemListSchema}</script>}
    </>
  )
}

// Page Export
//---------------------------------
function CollectionsProductFamilyPage({ data, pageContext, location }) {
  const [allActualData, setAllData] = useState(data.data.edges)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTab, setSelectedTab] = useState(0)

  const isBlackFriday =
    data.allContentfulPageProductFamily?.edges[0]?.node?.productFamily === 'Black Friday New'

  // ADD ADVANCED DESCRIPTION
  const advancedContent = data.allContentfulPageProductFamily.edges[0]?.node?.advancedContent || []
  const filteredAdvancedContent = advancedContent.filter(
    (target) => target?.image?.gatsbyImageData && target?.body
  )
  const isAdvancedContentDisplayed =
    data.allContentfulPageProductFamily.edges[0]?.node?.isAdvancedContentDisplayed

  const productIds =
    allActualData &&
    allActualData.reduce(
      (acc, { node }) => [...acc, ...node.products.map(({ shopifyId }) => shopifyId)],
      []
    )

  const {
    breadcrumb: { crumbs },
  } = pageContext

  const isPartiallyActive = ({ isPartiallyCurrent, isCurrent }) =>
    isPartiallyCurrent && isCurrent
      ? { className: 'breadcrumb__link breadcrumb__link__active' }
      : {}

  const family = pageContext?.productFamily

  // sort collections
  // ----------
  const allData = data.data.edges

  const isSoftware = location?.pathname?.includes('drone-software')

  const { rentalGroup, accessoriesGroupArray, trainingGroup, softwareGroup, repairGroup } =
    generateCollectionData(allData, isSoftware)

  useEffect(() => {
    const getActualProductQuantity = async () => {
      setIsLoading(true)

      const productInfo = (await nodeApi.getActualProductQuantity(productIds))?.filter(Boolean)

      if (productInfo?.length > 0) {
        setAllData(
          allActualData.map(({ node }) => ({
            node: {
              products: node.products.map((product) => {
                const actualInventory = productInfo.find(
                  ({ id }) => id === product.shopifyId
                )?.totalInventory
                return {
                  ...product,
                  totalInventory: actualInventory || product.totalInventory,
                }
              }),
            },
          }))
        )

        setIsLoading(false)
      }
    }

    getActualProductQuantity()
  }, [])

  const handleTabSelect = React.useCallback(
    (index, prevIndex, event, _title) => {
      if (typeof window !== 'undefined' && prevIndex !== index) {
        const title = _title || event?.target?.getAttribute('data-name')
        setSelectedTab(index)

        navigate(`${location.pathname}?selectedTab=${encodeURIComponent(title)}`, {
          replace: true,
        })
      }
    },
    [location]
  )

  useEffect(() => {
    const searchSelectedTabParam = new URLSearchParams(location.search).get('selectedTab')
    if (accessoriesGroupArray.length) {
      const tabIndex = accessoriesGroupArray.findIndex(
        (group) => group.title === searchSelectedTabParam
      )
      const index = tabIndex !== -1 ? tabIndex : 0

      setSelectedTab(index)
    }
  }, [location.search])

  const handleTabSelected = () => {
    if (typeof document !== 'undefined') {
      const searchSelectedTabParam = new URLSearchParams(location.search).get('selectedTab')

      if (selectedTab === 0 && accessoriesGroupArray.length) {
        const tabIndex = accessoriesGroupArray.findIndex(
          (group) => group.title === searchSelectedTabParam
        )
        const index = tabIndex !== -1 ? tabIndex : 0
        const tab = accessoriesGroupArray[index]

        handleTabSelect(index, null, null, tab?.title)
      }

      const tabs = document.getElementById('tabs')

      if (
        tabs &&
        !tabs?.classList?.contains('has-focused') &&
        selectedTab !== null &&
        searchSelectedTabParam
      ) {
        tabs.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        tabs.classList.add('has-focused')
      }
    }
  }

  useEffect(() => {
    handleTabSelected()
  }, [])

  function ProductCardElement({ target }) {
    return (
      <div className="col-xs-12 col-sm-6 col-lg-3 margin-80">
        <ProductCard
          productTitle={target.title}
          productDescription={target.metafield === null ? null : target.metafield.value}
          productPrice={
            target.priceRangeV2 === null ? null : target.priceRangeV2.minVariantPrice.amount
          }
          compareAtPrice={target.variants && target.variants[0]?.compareAtPrice}
          hidePrice={target?.tags?.includes('enquire only') || false}
          isNotify={target.tags && target.tags.some((tag) => tag === 'notify')}
          imgSrc={target.featuredImage.gatsbyImageData}
          altTag={target.title}
          productLink={`/products/${target.handle}`}
          isRental={target.productType.toLowerCase().includes('rental')}
          stockLevel={
            target.productType.toLowerCase().includes('rental') ? null : target.totalInventory
          }
          isLoading={isLoading}
        />
      </div>
    )
  }

  return (
    <div className="templateWrapper templateWrapper--alt">
      <section className="outer">
        <div className="inner">
          <Breadcrumbs
            crumbs={isBlackFriday ? crumbs : pageContext.breadCrumbs}
            crumbSeparator=" - "
            getProps={isPartiallyActive}
          />
        </div>
      </section>
      <MastHead
        imgSrc={
          data.allContentfulPageProductFamily.edges[0].node.mastheadBackgroundImage.gatsbyImageData
        }
        title={data.allContentfulPageProductFamily.edges[0].node.mastheadTitleLine}
        subtitle={data.allContentfulPageProductFamily.edges[0].node.mastheadSubtitle}
        type="Secondary"
      />
      <section className="outer">
        <div className="inner">
          {/* <TabsWrapper> */}
          <Tabs id="tabs" className="tabs" onSelect={handleTabSelect} selectedIndex={selectedTab}>
            <TabList>
              {accessoriesGroupArray.map((target, index) => (
                <Tab
                  onSelect={handleTabSelect}
                  data-name={target.title}
                  key={`${target.title}-${index}-top`}
                >
                  {target.title}
                </Tab>
              ))}
            </TabList>
            {accessoriesGroupArray.map((target, index) => (
              <TabPanel key={`${target.title}-${index}-body`}>
                <div className="row">
                  {target.value.map((targetInner, indexInner) => (
                    <ProductCardElement
                      key={`${target.title}-${indexInner}-body`}
                      target={targetInner}
                    />
                  ))}
                </div>
              </TabPanel>
            ))}
          </Tabs>
          {/* </TabsWrapper> */}

          {/* ADD ADVANCED DESCRIPTION */}
          <div className={`row ${!isAdvancedContentDisplayed && 'margin-80'}`}>
            {/* {dronesGroup.map((target, index) => (
              <ProductCardElement key={`drones-${index}`} target={target} />
            ))} */}

            {/* {rentalGroup.map((target, index) => (
              <ProductCardElement key={`rental-${index}`} target={target} />
            ))} */}

            {repairGroup.map((target, index) => (
              <ProductCardElement key={`repair-${index}`} target={target} />
            ))}

            {family === 'Course' &&
              trainingGroup.map((target, index) => (
                <ProductCardElement key={`course-${index}`} target={target} />
              ))}

            {family === 'Software' &&
              softwareGroup.map((target, index) => (
                <ProductCardElement key={`software-${index}`} target={target} />
              ))}
          </div>
        </div>
      </section>
      {/* ADD ADVANCED DESCRIPTION */}
      {isAdvancedContentDisplayed && filteredAdvancedContent.length > 0 && (
        <section className="outer">
          <div className="inner">
            {filteredAdvancedContent.map((target) => (
              <Lockup
                key={target.contentful_id}
                id={target.contentful_id}
                fieldsNames={{
                  heading: 'title',
                  subheading: 'subtitle',
                  body: 'body',
                  image: 'image',
                }}
                direction={target.direction}
                imgSrc={target.image?.gatsbyImageData}
                imgAltTag={target.image?.description}
                heading={target.title}
                subheader={target.subtitle}
                body={target.body}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

export default CollectionsProductFamilyPage

// GraphQL Call
//---------------------------------
export const data = graphql`
  query ($productFamily: String!) {
    allContentfulPageProductFamily(filter: { productFamily: { eq: $productFamily } }) {
      edges {
        node {
          pageSlug
          pageMetaTitle
          pageMetaDescription
          productFamily
          mastheadTitleLine
          mastheadSubtitle
          productLevel
          productFamily
          parentCategory
          mastheadBackgroundImage {
            gatsbyImageData(formats: WEBP, width: 1920)
            description
            url
          }
          isAdvancedContentDisplayed
          advancedContent {
            contentful_id
            title
            subtitle
            direction
            body {
              raw
            }
            image {
              gatsbyImageData
              description
            }
          }
        }
      }
    }
    data: allShopifyCollection(
      filter: {
        metafields: {
          elemMatch: { key: { eq: "Publish to Gatsby" }, value: { eq: $productFamily } }
        }
      }
    ) {
      edges {
        node {
          products {
            id
            shopifyId
            title
            description
            handle
            productType
            totalInventory
            tags
            tracksInventory
            variants {
              compareAtPrice
              price
            }
            priceRangeV2 {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            featuredImage {
              gatsbyImageData(formats: WEBP, width: 900)
              altText
              originalSrc
            }
            metafield(key: "excerpt", namespace: "content") {
              id
              value
            }
          }
        }
      }
    }
  }
`
// ADD ADVANCED DESCRIPTION
// isAdvancedContentDisplayed
//           advancedContent {
//             contentful_id
//             title
//             subtitle
//             direction
//             body {
//               raw
//             }
//             image {
//               gatsbyImageData
//               description
//             }
//           }
